import { z } from 'zod'
import { MOTIVOS_SAIDA } from './avisoDeLogin'

/**
 * Canal entre abas do MESMO navegador (faixa 25 §2, §6): entrega o access renovado e o sinal de saída.
 * Ver `canalEntreAbas.md`.
 */
export const NOME_DO_CANAL = 'omni-auth'

const MensagemSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('acesso'),
    access_token: z.string().min(1),
    expires_in: z.number().int().nonnegative(),
    origem: z.enum(['renovacao', 'troca-senha']),
    /** Esquema da emissão (faixa 33). Opcional: aba de build anterior não manda → quem recebe usa `Bearer`. */
    token_type: z.string().optional(),
  }),
  z.object({ tipo: z.literal('saiu'), motivo: z.enum(MOTIVOS_SAIDA) }),
])

export type MensagemEntreAbas = z.infer<typeof MensagemSchema>

/** A porta de um canal: o `BroadcastChannel` real, ou o barramento falso dos testes. */
export interface PortaDoCanal {
  enviar(mensagem: MensagemEntreAbas): void
  aoReceber(ouvinte: (dado: unknown) => void): () => void
}

export type FabricaDePorta = () => PortaDoCanal | null

type Ouvinte = (mensagem: MensagemEntreAbas) => void

let fabricaInjetada: FabricaDePorta | null = null
/** `undefined` = ainda não abriu; `null` = sem suporte (a aba segue sozinha). */
let porta: PortaDoCanal | null | undefined
let desligarPorta: (() => void) | null = null
const ouvintes = new Set<Ouvinte>()

function portaReal(): PortaDoCanal | null {
  if (typeof BroadcastChannel !== 'function') return null
  const canal = new BroadcastChannel(NOME_DO_CANAL)
  return {
    enviar: (mensagem) => canal.postMessage(mensagem),
    aoReceber: (ouvinte) => {
      const aoChegar = (evento: MessageEvent) => ouvinte(evento.data)
      canal.addEventListener('message', aoChegar)
      return () => canal.removeEventListener('message', aoChegar)
    },
  }
}

function entregar(dado: unknown): void {
  const lida = MensagemSchema.safeParse(dado)
  if (!lida.success) {
    // Aba de outra versão do front durante o deploy, ou lixo. O conteúdo NÃO vai pro log: pode ter um access (I1).
    console.warn('Mensagem de sessão entre abas fora do contrato; ignorada.')
    return
  }
  for (const ouvir of [...ouvintes]) ouvir(lida.data)
}

function abrir(): PortaDoCanal | null {
  if (porta === undefined) {
    porta = (fabricaInjetada ?? portaReal)()
    desligarPorta = porta?.aoReceber(entregar) ?? null
  }
  return porta
}

export function publicarNoCanal(mensagem: MensagemEntreAbas): void {
  abrir()?.enviar(mensagem)
}

export function ouvirCanal(ouvinte: Ouvinte): () => void {
  abrir()
  ouvintes.add(ouvinte)
  return () => {
    ouvintes.delete(ouvinte)
  }
}

/** Só teste: troca a fábrica e descarta a porta e os ouvintes atuais. `null` volta ao `BroadcastChannel` real. */
export function definirCanal(fabrica: FabricaDePorta | null): void {
  desligarPorta?.()
  desligarPorta = null
  porta = undefined
  fabricaInjetada = fabrica
  ouvintes.clear()
}
